import { apiClient } from '@api'

/**
 * Helper function to flatten nested filter objects for URL params
 * Converts { dateRange: { preset: 'last_30_days' } } to { date_range: 'last_30_days' }
 *
 * @param {Object} filters - Filter object that may contain nested values
 * @returns {Object} Flattened filter object suitable for URLSearchParams
 */
const flattenFilters = (filters = {}) => {
	const result = {}

	for (const [key, value] of Object.entries(filters)) {
		if (value === null || value === undefined) {
			continue
		}

		// Handle dateRange object specially
		if (key === 'dateRange' && typeof value === 'object') {
			if (value.preset) {
				result['date_range'] = value.preset
			}
			if (value.startDate) {
				result['start_date'] = value.startDate
			}
			if (value.endDate) {
				result['end_date'] = value.endDate
			}
		} else if (typeof value === 'object' && !Array.isArray(value)) {
			// For other nested objects, flatten with underscore notation
			for (const [nestedKey, nestedValue] of Object.entries(value)) {
				if (nestedValue !== null && nestedValue !== undefined) {
					result[`${key}_${nestedKey}`] = nestedValue
				}
			}
		} else if (Array.isArray(value)) {
			// Handle arrays - join with commas
			result[key] = value.join(',')
		} else {
			// Simple values pass through
			result[key] = value
		}
	}

	return result
}

/**
 * Build query string from filters
 *
 * @param {Object} filters - Filter object
 * @returns {string} Query string for URL
 */
const buildQueryString = (filters = {}) => {
	const flattened = flattenFilters(filters)
	const params = new URLSearchParams(flattened).toString()
	return params ? `?${params}` : ''
}

/**
 * Report Service
 *
 * API service for report data fetching and export.
 * Provides methods for all report types across different roles.
 */
const reportService = {
	// ====================================================================
	// GENERAL REPORT ENDPOINTS
	// ====================================================================

	/**
	 * Get available report types for current user
	 * @returns {Promise} API response with report types
	 */
	getReportTypes: () => {
		return apiClient.get('/reports/types')
	},

	/**
	 * Get filter options for a report type
	 * @param {string} reportType - Report type identifier
	 * @returns {Promise} API response with filter options
	 */
	getFilterOptions: (reportType) => {
		return apiClient.get(`/reports/filters/${reportType}`)
	},

	// ====================================================================
	// PLATFORM REPORTS (Super Admin)
	// ====================================================================

	/**
	 * Get platform overview report
	 * @param {Object} filters - Report filters
	 * @returns {Promise} API response with platform metrics
	 */
	getPlatformOverview: (filters = {}) => {
		return apiClient.get(`/reports/platform/overview${buildQueryString(filters)}`)
	},

	/**
	 * Get companies report for platform
	 * @param {Object} filters - Report filters
	 * @returns {Promise} API response with company list
	 */
	getPlatformCompanies: (filters = {}) => {
		return apiClient.get(`/reports/platform/companies${buildQueryString(filters)}`)
	},

	/**
	 * Get revenue analytics report
	 * @param {Object} filters - Report filters
	 * @returns {Promise} API response with revenue metrics
	 */
	getPlatformRevenue: (filters = {}) => {
		return apiClient.get(`/reports/platform/revenue${buildQueryString(filters)}`)
	},

	/**
	 * Get platform activity report
	 * @param {Object} filters - Report filters
	 * @returns {Promise} API response with activity data
	 */
	getPlatformActivity: (filters = {}) => {
		return apiClient.get(`/reports/platform/activity${buildQueryString(filters)}`)
	},

	/**
	 * Get platform projects report
	 * @param {Object} filters - Report filters
	 * @returns {Promise} API response with project metrics
	 */
	getPlatformProjects: (filters = {}) => {
		return apiClient.get(`/reports/platform/projects${buildQueryString(filters)}`)
	},

	/**
	 * Get platform tasks report
	 * @param {Object} filters - Report filters
	 * @returns {Promise} API response with task metrics
	 */
	getPlatformTasks: (filters = {}) => {
		return apiClient.get(`/reports/platform/tasks${buildQueryString(filters)}`)
	},

	/**
	 * Get platform health report
	 * @returns {Promise} API response with system health data
	 */
	getPlatformHealth: () => {
		return apiClient.get('/reports/platform/health')
	},

	// ====================================================================
	// COMPANY REPORTS
	// ====================================================================

	/**
	 * Get company overview report
	 * @param {Object} filters - Report filters
	 * @returns {Promise} API response with company metrics
	 */
	getCompanyOverview: (filters = {}) => {
		return apiClient.get(`/reports/company/overview${buildQueryString(filters)}`)
	},

	/**
	 * Get company projects report
	 * @param {Object} filters - Report filters
	 * @returns {Promise} API response with project portfolio
	 */
	getCompanyProjects: (filters = {}) => {
		return apiClient.get(`/reports/company/projects${buildQueryString(filters)}`)
	},

	/**
	 * Get company compliance report
	 * @param {Object} filters - Report filters
	 * @returns {Promise} API response with compliance metrics
	 */
	getCompanyCompliance: (filters = {}) => {
		return apiClient.get(`/reports/company/compliance${buildQueryString(filters)}`)
	},

	/**
	 * Get company team report
	 * @param {Object} filters - Report filters
	 * @returns {Promise} API response with team performance
	 */
	getCompanyTeam: (filters = {}) => {
		return apiClient.get(`/reports/company/team${buildQueryString(filters)}`)
	},

	/**
	 * Get company SLA report
	 * @param {Object} filters - Report filters
	 * @returns {Promise} API response with SLA metrics
	 */
	getCompanySLA: (filters = {}) => {
		return apiClient.get(`/reports/company/sla${buildQueryString(filters)}`)
	},

	/**
	 * Get company tasks report
	 * @param {Object} filters - Report filters
	 * @returns {Promise} API response with task metrics
	 */
	getCompanyTasks: (filters = {}) => {
		return apiClient.get(`/reports/company/tasks${buildQueryString(filters)}`)
	},

	/**
	 * Get company workflows report
	 * @param {Object} filters - Report filters
	 * @returns {Promise} API response with workflow metrics
	 */
	getCompanyWorkflows: (filters = {}) => {
		return apiClient.get(`/reports/company/workflows${buildQueryString(filters)}`)
	},

	/**
	 * Get company audit report
	 * @param {Object} filters - Report filters
	 * @returns {Promise} API response with audit data
	 */
	getCompanyAudit: (filters = {}) => {
		return apiClient.get(`/reports/company/audit${buildQueryString(filters)}`)
	},

	/**
	 * Get company documents report
	 * @param {Object} filters - Report filters
	 * @returns {Promise} API response with document metrics
	 */
	getCompanyDocuments: (filters = {}) => {
		return apiClient.get(`/reports/company/documents${buildQueryString(filters)}`)
	},

	/**
	 * Get company gap analysis report
	 * @param {Object} filters - Report filters
	 * @returns {Promise} API response with gap analysis
	 */
	getGapAnalysis: (filters = {}) => {
		return apiClient.get(`/reports/company/gap-analysis${buildQueryString(filters)}`)
	},

	/**
	 * Get Senior Management Compliance Dashboard report
	 * @param {Object} filters - Report filters
	 * @returns {Promise} API response with aggregated senior management metrics
	 */
	getSeniorManagementReport: (filters = {}) => {
		return apiClient.get(`/reports/company/senior-management${buildQueryString(filters)}`)
	},

	/**
	 * Get Compliance Manager personal dashboard report
	 * @param {Object} filters - Report filters
	 * @returns {Promise} API response with scope-restricted compliance manager metrics
	 */
	getComplianceManagerReport: (filters = {}) => {
		return apiClient.get(`/reports/company/compliance-manager${buildQueryString(filters)}`)
	},

	// ====================================================================
	// PROJECT REPORTS
	// ====================================================================

	/**
	 * Get project detail report
	 * @param {number} projectId - Project ID
	 * @param {Object} filters - Report filters
	 * @returns {Promise} API response with project details
	 */
	getProjectDetail: (projectId, filters = {}) => {
		return apiClient.get(`/reports/project/${projectId}/detail${buildQueryString(filters)}`)
	},

	/**
	 * Get project timeline report
	 * @param {number} projectId - Project ID
	 * @returns {Promise} API response with timeline data
	 */
	getProjectTimeline: (projectId) => {
		return apiClient.get(`/reports/project/${projectId}/timeline`)
	},

	/**
	 * Get project tasks report
	 * @param {number} projectId - Project ID
	 * @param {Object} filters - Report filters
	 * @returns {Promise} API response with task list
	 */
	getProjectTasks: (projectId, filters = {}) => {
		return apiClient.get(`/reports/project/${projectId}/tasks${buildQueryString(filters)}`)
	},

	/**
	 * Get project team report
	 * @param {number} projectId - Project ID
	 * @returns {Promise} API response with team data
	 */
	getProjectTeam: (projectId) => {
		return apiClient.get(`/reports/project/${projectId}/team`)
	},

	/**
	 * Get project compliance report
	 * @param {number} projectId - Project ID
	 * @returns {Promise} API response with compliance data
	 */
	getProjectCompliance: (projectId) => {
		return apiClient.get(`/reports/project/${projectId}/compliance`)
	},

	// ====================================================================
	// USER REPORTS
	// ====================================================================

	/**
	 * Get current user's tasks report
	 * @param {Object} filters - Report filters
	 * @returns {Promise} API response with task list
	 */
	getMyTasks: (filters = {}) => {
		return apiClient.get(`/reports/user/my-tasks${buildQueryString(filters)}`)
	},

	/**
	 * Get current user's approvals report
	 * @param {Object} filters - Report filters
	 * @returns {Promise} API response with approvals list
	 */
	getMyApprovals: (filters = {}) => {
		return apiClient.get(`/reports/user/my-approvals${buildQueryString(filters)}`)
	},

	/**
	 * Get current user's performance report
	 * @param {Object} filters - Report filters
	 * @returns {Promise} API response with performance data
	 */
	getMyPerformance: (filters = {}) => {
		return apiClient.get(`/reports/user/performance${buildQueryString(filters)}`)
	},

	/**
	 * Get user summary report
	 * @param {number} userId - User ID
	 * @returns {Promise} API response with user summary
	 */
	getUserSummary: (userId) => {
		return apiClient.get(`/reports/user/${userId}/summary`)
	},

	/**
	 * Get user performance report
	 * @param {number} userId - User ID
	 * @param {Object} filters - Report filters
	 * @returns {Promise} API response with performance data
	 */
	getUserPerformance: (userId, filters = {}) => {
		return apiClient.get(`/reports/user/${userId}/performance${buildQueryString(filters)}`)
	},

	// ====================================================================
	// EXPORT ENDPOINTS
	// ====================================================================

	/**
	 * Export report to specified format
	 * @param {string} reportType - Report type identifier
	 * @param {string} format - Export format (pdf, excel, csv)
	 * @param {Object} filters - Report filters
	 * @param {Object} config - Export configuration
	 * @returns {Promise} Initiates download
	 */
	exportReport: async (reportType, format, filters = {}, config = {}) => {
		const response = await apiClient.post('/reports/export', {
			report_type: reportType,
			format,
			filters,
			config,
		}, {
			responseType: 'blob'
		})

		// Create download link
		const blob = new Blob([response], { type: getMimeType(format) })
		const url = window.URL.createObjectURL(blob)
		const link = document.createElement('a')
		link.href = url
		link.download = `${reportType}-${Date.now()}.${getExtension(format)}`
		document.body.appendChild(link)
		link.click()
		document.body.removeChild(link)
		window.URL.revokeObjectURL(url)

		return true
	},

	/**
	 * Export report and get download URL
	 * @param {string} reportType - Report type identifier
	 * @param {string} format - Export format (pdf, excel, csv)
	 * @param {Object} filters - Report filters
	 * @param {Object} config - Export configuration
	 * @returns {Promise} API response with download URL
	 */
	exportToFile: (reportType, format, filters = {}, config = {}) => {
		return apiClient.post('/reports/export/file', {
			report_type: reportType,
			format,
			filters,
			config,
		})
	},

	/**
	 * Download exported file
	 * @param {string} token - Download token
	 * @returns {Promise} Initiates download
	 */
	downloadExport: async (token) => {
		const response = await apiClient.get(`/reports/download/${token}`, {
			responseType: 'blob'
		})

		// Create download link
		const url = window.URL.createObjectURL(new Blob([response]))
		const link = document.createElement('a')
		link.href = url
		link.download = 'report'
		document.body.appendChild(link)
		link.click()
		document.body.removeChild(link)
		window.URL.revokeObjectURL(url)

		return true
	},
}

// Helper: Get MIME type for format
const getMimeType = (format) => {
	const types = {
		pdf: 'application/pdf',
		excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
		xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
		csv: 'text/csv',
	}
	return types[format] || 'application/octet-stream'
}

// Helper: Get file extension for format
const getExtension = (format) => {
	const extensions = {
		pdf: 'pdf',
		excel: 'xlsx',
		xlsx: 'xlsx',
		csv: 'csv',
	}
	return extensions[format] || format
}

export default reportService
